import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import CompletedActivityCard from "../common/CompletedActivityCard";
import CompletedActivityCardSkeleton from "../common/CompletedActivityCardSkeleton";
import {
  fetchRegionalQuestionnaireHistory,
  fetchRegionalQuestionnaireResponses,
} from "../../utils/backend/pdxBackendClient";
import { QUESTIONNAIRES } from "../../utils/constants";
import formatAnswer from "../../utils/form/formatAnswer";
import actQuestions from "../../utils/questionnaireQuestions/questions/act";
import caatQuestions from "../../utils/questionnaireQuestions/questions/caat";
import catQuestions from "../../utils/questionnaireQuestions/questions/cat";
import monthlyCheckInQuestions from "../../utils/questionnaireQuestions/questions/monthly-check-in";
import rScaleQuestions from "../../utils/questionnaireQuestions/questions/r-scale";
import sgrqQuestions from "../../utils/questionnaireQuestions/questions/sgrq";
import weeklyCheckInQuestions from "../../utils/questionnaireQuestions/questions/weekly-check-in";
import { formatDateParts } from "../../utils/results/recordingIdTime";
import {
  getQuestionnaireMaxScore,
  hasReportableScore,
  shouldShowQuestionnaireScoreToPatient,
} from "../../utils/questionnaireQuestions/questionnaireScores";
import { buildQuestionnaireReviewPath } from "../../utils/questionnaireQuestions/reviewNavigation";

const QUESTIONNAIRE_META = Object.fromEntries(
  QUESTIONNAIRES.map(({ slug, labelKey }) => [slug, { labelKey }]),
);
const HISTORY_LIMIT = 50;

function normalizeOptionalString(value) {
  if (value === undefined || value === null) {
    return null;
  }

  const normalizedValue = String(value).trim();
  return normalizedValue.length > 0 ? normalizedValue : null;
}

const QUESTIONNAIRE_QUESTION_MAPS = {
  act: new Map(
    actQuestions
      .map((question) => [normalizeOptionalString(question?.id), question])
      .filter(([id]) => id)
      .map(([id, question]) => [id.toLowerCase(), question]),
  ),
  caat: new Map(
    caatQuestions
      .map((question) => [normalizeOptionalString(question?.id), question])
      .filter(([id]) => id)
      .map(([id, question]) => [id.toLowerCase(), question]),
  ),
  cat: new Map(
    catQuestions
      .map((question) => [normalizeOptionalString(question?.id), question])
      .filter(([id]) => id)
      .map(([id, question]) => [id.toLowerCase(), question]),
  ),
  "monthly-check-in": new Map(
    monthlyCheckInQuestions
      .map((question) => [normalizeOptionalString(question?.id), question])
      .filter(([id]) => id)
      .map(([id, question]) => [id.toLowerCase(), question]),
  ),
  "r-scale": new Map(
    rScaleQuestions
      .map((question) => [normalizeOptionalString(question?.id), question])
      .filter(([id]) => id)
      .map(([id, question]) => [id.toLowerCase(), question]),
  ),
  sgrq: new Map(
    sgrqQuestions
      .map((question) => [normalizeOptionalString(question?.id), question])
      .filter(([id]) => id)
      .map(([id, question]) => [id.toLowerCase(), question]),
  ),
  "weekly-check-in": new Map(
    weeklyCheckInQuestions
      .map((question) => [normalizeOptionalString(question?.id), question])
      .filter(([id]) => id)
      .map(([id, question]) => [id.toLowerCase(), question]),
  ),
};
const QUESTIONNAIRE_AUXILIARY_FIELD_IDS = new Set(
  Object.values(QUESTIONNAIRE_QUESTION_MAPS)
    .flatMap((questionMap) => Array.from(questionMap.values()))
    .flatMap((question) => [normalizeOptionalString(question?.freeTextFieldId)])
    .filter(Boolean)
    .map((fieldId) => fieldId.toLowerCase()),
);

function translateWithFallback(
  questionnaireTranslate,
  commonTranslate,
  key,
  options,
) {
  if (typeof key !== "string" || !key.trim()) {
    return "";
  }

  const questionnaireValue =
    typeof questionnaireTranslate === "function"
      ? questionnaireTranslate(key, options)
      : key;

  if (questionnaireValue && questionnaireValue !== key) {
    return questionnaireValue;
  }

  const commonValue =
    typeof commonTranslate === "function" ? commonTranslate(key, options) : key;

  if (commonValue && commonValue !== key) {
    return commonValue;
  }

  return key;
}

function formatFallbackAnswer(value) {
  if (value === undefined || value === null || value === "") {
    return "N/A";
  }

  if (value === true) {
    return "Yes";
  }

  if (value === false) {
    return "No";
  }

  if (Array.isArray(value)) {
    const items = value
      .map((entry) => normalizeOptionalString(entry))
      .filter(Boolean);
    return items.length > 0 ? items.join(", ") : "N/A";
  }

  if (typeof value === "object") {
    return JSON.stringify(value, null, 2);
  }

  return String(value);
}

function humanizeResponseKey(key) {
  const normalizedKey = normalizeOptionalString(key);

  if (!normalizedKey) {
    return "Question";
  }

  return normalizedKey
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[-_]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/^./u, (character) => character.toUpperCase());
}

function resolveQuestionTitle(question, questionnaireTranslate, commonTranslate) {
  if (question?.titleKey) {
    const translatedTitle = translateWithFallback(
      questionnaireTranslate,
      commonTranslate,
      question.titleKey,
    );

    if (translatedTitle && translatedTitle !== question.titleKey) {
      return translatedTitle;
    }
  }

  return normalizeOptionalString(question?.title) || humanizeResponseKey(question?.id);
}

function resolveFallbackQuestionLabel(
  questionnaireId,
  responseKey,
  questionnaireTranslate,
  commonTranslate,
) {
  const normalizedQuestionnaireId = normalizeOptionalString(questionnaireId);
  const normalizedResponseKey = normalizeOptionalString(responseKey);

  if (!normalizedResponseKey) {
    return "Question";
  }

  const question = normalizedQuestionnaireId
    ? QUESTIONNAIRE_QUESTION_MAPS[normalizedQuestionnaireId.toLowerCase()]?.get(
        normalizedResponseKey.toLowerCase(),
      )
    : null;

  if (question) {
    return resolveQuestionTitle(
      question,
      questionnaireTranslate,
      commonTranslate,
    );
  }

  return humanizeResponseKey(normalizedResponseKey);
}

function getFallbackQuestionDefinition(questionnaireId, responseKey) {
  const normalizedQuestionnaireId = normalizeOptionalString(questionnaireId);
  const normalizedResponseKey = normalizeOptionalString(responseKey);

  if (!normalizedQuestionnaireId || !normalizedResponseKey) {
    return null;
  }

  return QUESTIONNAIRE_QUESTION_MAPS[normalizedQuestionnaireId.toLowerCase()]?.get(
    normalizedResponseKey.toLowerCase(),
  );
}

function buildResponseFormData(responseItems) {
  return (Array.isArray(responseItems) ? responseItems : []).reduce(
    (formData, item) => {
      const key = normalizeOptionalString(item?.key);

      if (!key) {
        return formData;
      }

      formData[key] = item?.value;
      return formData;
    },
    {},
  );
}

function resolveFallbackAnswerText(
  question,
  formData,
  value,
  questionnaireTranslate,
  commonTranslate,
) {
  if (!question) {
    return formatFallbackAnswer(value);
  }

  return formatAnswer(question, formData, (key, options) =>
    translateWithFallback(questionnaireTranslate, commonTranslate, key, options),
  );
}

function getQuestionnaireRecordTimestamp(record) {
  return (
    record?.submissionProvenance?.completedAt ||
    record?.takenAt ||
    record?.submittedAt ||
    record?.clientSubmittedAt ||
    record?.createdAt ||
    null
  );
}

function normalizeHistoryEntry(record, locale, questionnaireAssignments) {
  const questionnaireId =
    typeof record?.questionnaireId === "string"
      ? record.questionnaireId.trim().toLowerCase()
      : "";

  if (!questionnaireId) {
    return null;
  }

  const timestamp = getQuestionnaireRecordTimestamp(record);
  const date = timestamp ? new Date(timestamp) : null;
  const { time, date: formattedDate } = formatDateParts(date, locale);
  const meta = QUESTIONNAIRE_META[questionnaireId];
  const maxScore =
    typeof record?.maxScore === "number" && Number.isFinite(record.maxScore)
      ? record.maxScore
      : getQuestionnaireMaxScore(questionnaireId);

  return {
    docId:
      typeof record?.docId === "string" && record.docId.trim()
        ? record.docId
        : null,
    questionnaireId,
    labelKey: meta?.labelKey ?? null,
    fallbackLabel:
      record?.reviewSnapshot?.questionnaireShortLabel ||
      record?.reviewSnapshot?.questionnaireTitle ||
      questionnaireId,
    time,
    date: formattedDate,
    score: hasReportableScore(
      questionnaireId,
      record?.score,
      shouldShowQuestionnaireScoreToPatient(
        questionnaireId,
        questionnaireAssignments,
      ),
    )
      ? record.score
      : null,
    maxScore,
  };
}

function buildReviewItems(
  record,
  questionnaireTranslate = () => "",
  commonTranslate = () => "",
) {
  const snapshotItems = Array.isArray(record?.reviewSnapshot?.items)
    ? record.reviewSnapshot.items
    : [];

  if (snapshotItems.length > 0) {
    return snapshotItems
      .map((item) => ({
        id: normalizeOptionalString(item?.id) || item?.label || "question",
        label: normalizeOptionalString(item?.label) || "Question",
        answerText: normalizeOptionalString(item?.answerText) || "N/A",
      }))
      .filter(Boolean);
  }

  const responseItems = Array.isArray(record?.responseItems)
    ? record.responseItems
    : [];

  if (responseItems.length > 0) {
    const formData = buildResponseFormData(responseItems);

    return responseItems.map((item) => ({
      id: normalizeOptionalString(item?.key) || "question",
      label: resolveFallbackQuestionLabel(
        record?.questionnaireId,
        item?.key,
        questionnaireTranslate,
        commonTranslate,
      ),
      answerText: resolveFallbackAnswerText(
        getFallbackQuestionDefinition(record?.questionnaireId, item?.key),
        formData,
        item?.value,
        questionnaireTranslate,
        commonTranslate,
      ),
    }));
  }

  const rawResponses =
    record?.responses && typeof record.responses === "object"
      ? record.responses
      : record?.payload?.responses &&
          typeof record.payload.responses === "object"
      ? record.payload.responses
      : {};

  return Object.entries(rawResponses)
    .filter(([key]) => {
      const normalizedKey = normalizeOptionalString(key);

      return normalizedKey
        ? !QUESTIONNAIRE_AUXILIARY_FIELD_IDS.has(normalizedKey.toLowerCase())
        : false;
    })
    .map(([key, value]) => ({
      id: key,
      label: resolveFallbackQuestionLabel(
        record?.questionnaireId,
        key,
        questionnaireTranslate,
        commonTranslate,
      ),
      answerText: resolveFallbackAnswerText(
        getFallbackQuestionDefinition(record?.questionnaireId, key),
        rawResponses,
        value,
        questionnaireTranslate,
        commonTranslate,
      ),
    }));
}

export default function QuestionnaireResponseReview({
  questionnaireId,
  docId,
  questionnaireAssignments = [],
}) {
  const { t, i18n } = useTranslation();
  const { t: tQuestionnaire } = useTranslation(questionnaireId || "common");
  const locale = i18n.resolvedLanguage || i18n.language || "en-GB";
  const [state, setState] = useState({
    loading: true,
    error: null,
    record: null,
    history: [],
  });

  useEffect(() => {
    if (!questionnaireId || !docId) {
      setState({
        loading: false,
        error: new Error("missing-review-context"),
        record: null,
        history: [],
      });
      return;
    }

    let cancelled = false;

    setState((previousState) => ({
      ...previousState,
      loading: true,
      error: null,
    }));

    Promise.all([
      fetchRegionalQuestionnaireResponses(questionnaireId, { docIds: [docId] }),
      fetchRegionalQuestionnaireHistory({
        questionnaireId,
        limit: HISTORY_LIMIT,
      }),
    ])
      .then(([detailResponse, historyResponse]) => {
        if (cancelled) {
          return;
        }

        const record = Array.isArray(detailResponse?.questionnaires)
          ? detailResponse.questionnaires[0] || null
          : null;

        setState({
          loading: false,
          error: record ? null : new Error("questionnaire-review-not-found"),
          record,
          history: Array.isArray(historyResponse?.questionnaires)
            ? historyResponse.questionnaires
            : [],
        });
      })
      .catch((error) => {
        if (cancelled) {
          return;
        }

        setState({
          loading: false,
          error,
          record: null,
          history: [],
        });
      });

    return () => {
      cancelled = true;
    };
  }, [docId, questionnaireId]);

  const record = state.record;
  const historyEntries = useMemo(
    () =>
      state.history
        .map((entry) =>
          normalizeHistoryEntry(entry, locale, questionnaireAssignments),
        )
        .filter(Boolean),
    [locale, questionnaireAssignments, state.history],
  );
  const reviewItems = useMemo(
    () => buildReviewItems(record, tQuestionnaire, t),
    [record, t, tQuestionnaire],
  );
  const selectedHistoryEntry = useMemo(
    () => normalizeHistoryEntry(record, locale, questionnaireAssignments),
    [locale, questionnaireAssignments, record],
  );
  const score = typeof record?.score === "number" ? record.score : null;
  const maxScore =
    typeof record?.maxScore === "number" && Number.isFinite(record.maxScore)
      ? record.maxScore
      : getQuestionnaireMaxScore(questionnaireId);
  const showScore = hasReportableScore(
    questionnaireId,
    score,
    shouldShowQuestionnaireScoreToPatient(
      questionnaireId,
      questionnaireAssignments,
    ),
  );
  const completedAt = getQuestionnaireRecordTimestamp(record);
  const completedDate = completedAt ? new Date(completedAt) : null;
  const completedParts = formatDateParts(completedDate, locale);
  const questionnaireTitle =
    record?.reviewSnapshot?.questionnaireTitle ||
    (selectedHistoryEntry?.labelKey
      ? t(selectedHistoryEntry.labelKey, selectedHistoryEntry.fallbackLabel)
      : selectedHistoryEntry?.fallbackLabel || questionnaireId);

  if (state.loading) {
    return (
      <div className="w-full max-w-lg p-4 flex flex-col gap-3">
        <CompletedActivityCardSkeleton showDetails />
        <CompletedActivityCardSkeleton showDetails />
        <CompletedActivityCardSkeleton showDetails />
      </div>
    );
  }

  if (state.error || !record) {
    return (
      <div className="w-full max-w-lg p-4">
        <section className="rounded-2xl bg-greyscale-1 p-4 text-left">
          <h2 className="subtitle-text mb-2">
            {t("questionnaireReview.unavailableTitle", "Responses unavailable")}
          </h2>
          <p className="muted-text">
            {t(
              "questionnaireReview.unavailable",
              "We couldn't load this questionnaire submission right now.",
            )}
          </p>
        </section>
      </div>
    );
  }

  return (
    <div className="w-full max-w-lg min-h-full flex flex-col gap-4 p-4">
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-1">
          <p className="muted-text text-sm">
            {t("questionnaireReview.submittedTitle", "Submitted responses")}
          </p>
          <h1 className="title-text whitespace-pre-line">
            {questionnaireTitle}
          </h1>
          <p className="muted-text text-sm">
            {[completedParts.time, completedParts.date]
              .filter(Boolean)
              .join(" ")}
          </p>
        </div>

        {showScore ? (
          <div className="rounded-2xl bg-card px-4 py-3 shadow">
            <p className="muted-text text-sm">
              {t("questionnaireReview.scoreTitle", "Score")}
            </p>
            <p className="text-[2rem] font-semibold leading-none text-typography">
              {score}
              {maxScore !== null ? (
                <span className="ml-2 text-base font-medium text-greyscale-3">
                  / {maxScore}
                </span>
              ) : null}
            </p>
          </div>
        ) : null}

        <div className="flex flex-col gap-3">
          <h2 className="text-base font-semibold text-typography">
            {t("questionnaireReview.answersTitle", "Your answers")}
          </h2>

          {reviewItems.length > 0 ? (
            reviewItems.map((item) => (
              <article
                key={item.id}
                className="rounded-2xl bg-card px-4 py-3 shadow text-left"
              >
                <h3 className="text-sm font-semibold text-typography">
                  {item.label}
                </h3>
                <div className="mt-2 flex flex-col gap-1">
                  {String(item.answerText || "N/A")
                    .split("\n")
                    .filter(Boolean)
                    .map((line, index) => (
                      <p
                        key={`${item.id}-${index}`}
                        className="whitespace-pre-line text-sm text-typography"
                      >
                        {line}
                      </p>
                    ))}
                </div>
              </article>
            ))
          ) : (
            <div className="rounded-2xl bg-card px-4 py-4 text-sm text-greyscale-3 shadow">
              {t(
                "questionnaireReview.noAnswers",
                "No answer breakdown is available for this submission.",
              )}
            </div>
          )}
        </div>
      </div>

      <section
        className="rounded-t-2xl bg-greyscale-1 p-4 flex flex-col gap-3"
        aria-label={t("questionnaireReview.historyTitle", "Previous submissions")}
      >
        <div className="flex items-center gap-3">
          <h2 className="subtitle-text text-left">
            {t("questionnaireReview.historyTitle", "Previous submissions")}
          </h2>
        </div>

        {historyEntries.length > 0 ? (
          <div className="flex flex-col gap-3">
            {historyEntries.map((entry) => {
              const label = entry.labelKey
                ? t(entry.labelKey, entry.fallbackLabel)
                : entry.fallbackLabel;
              const showEntryScore =
                entry.score !== null && entry.maxScore !== null;

              return (
                <CompletedActivityCard
                  key={entry.docId || `${entry.questionnaireId}:${entry.time}`}
                  label={label}
                  meta={[entry.time, entry.date].filter(Boolean).join(" ")}
                  icon="assignment_turned_in"
                  to={
                    entry.docId
                      ? buildQuestionnaireReviewPath(
                          entry.questionnaireId,
                          entry.docId,
                        )
                      : null
                  }
                  trailing={
                    showEntryScore ? (
                      <span className="tabular-nums text-sm font-bold text-typography">
                        {entry.score}
                        <span className="muted-text font-medium">
                          {" "}
                          / {entry.maxScore}
                        </span>
                      </span>
                    ) : null
                  }
                />
              );
            })}
          </div>
        ) : (
          <p className="muted-text text-sm">
            {t(
              "questionnaireReview.historyEmpty",
              "No submitted history is available for this questionnaire yet.",
            )}
          </p>
        )}
      </section>
    </div>
  );
}
